import React, { useRef, useEffect } from 'react';
import { Bold, Italic, Strikethrough, Underline, List, ListOrdered } from 'lucide-react';

interface RichTextToolbarProps {
  /** Id of the textarea/contenteditable this toolbar controls */
  textareaId: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

/**
 * Formatting toolbar for description/profile (Bold, Italic, Strikethrough, Underline, bullet/number list).
 * Uses selectionchange to keep last editor selection, and onMouseDownCapture + preventDefault so toolbar
 * clicks don't steal focus/selection. Changes sync to both editor and preview.
 */
const RichTextToolbar: React.FC<RichTextToolbarProps> = ({
  textareaId,
  value,
  onChange,
  disabled = false,
}) => {
  const savedSelectionRef = useRef<{ start: number; end: number } | null>(null);
  const savedRangeRef = useRef<Range | null>(null);

  const getTarget = () => {
    if (!textareaId || typeof document === 'undefined') return null;
    return document.getElementById(textareaId);
  };

  const isContentEditable = (el: Element | null): el is HTMLElement =>
    !!el && (el as HTMLElement).isContentEditable === true;

  // Keep last selection when it's inside the editor (backup for toolbar click)
  useEffect(() => {
    const handler = () => {
      const target = getTarget();
      if (!target || !target.isConnected || !isContentEditable(target)) return;
      const sel = document.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      const container = range.commonAncestorContainer;
      const node = container.nodeType === Node.TEXT_NODE ? container.parentNode : container;
      if (node && target.contains(node)) {
        try {
          savedRangeRef.current = range.cloneRange();
        } catch {
          savedRangeRef.current = null;
        }
      }
      // When selection moves outside editor, keep previous savedRangeRef
    };
    document.addEventListener('selectionchange', handler);
    return () => document.removeEventListener('selectionchange', handler);
  }, [textareaId]);

  const captureSelection = (e: React.MouseEvent | React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const target = getTarget();
    if (!target || !target.isConnected) {
      savedSelectionRef.current = null;
      savedRangeRef.current = null;
      return;
    }
    if (isContentEditable(target)) {
      const sel = document.getSelection();
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        const container = range.commonAncestorContainer;
        const node = container.nodeType === Node.TEXT_NODE ? container.parentNode : container;
        if (node && target.contains(node)) {
          try {
            savedRangeRef.current = range.cloneRange();
          } catch {
            savedRangeRef.current = null;
          }
        }
      }
      savedSelectionRef.current = null;
    } else {
      const ta = target as HTMLTextAreaElement;
      savedSelectionRef.current = { start: ta.selectionStart, end: ta.selectionEnd };
      savedRangeRef.current = null;
    }
  };

  const handleToolbarMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    captureSelection(e);
  };

  /** Wrap only the exact selection in a tag (so Preview bolds the same text as in section) */
  const wrapSelectionInTag = (tagName: 'strong' | 'em' | 'span', spanClass?: string, execCmd?: string) => {
    const el = getTarget();
    if (!el || !el.isConnected || !isContentEditable(el)) return;
    if (!savedRangeRef.current) return;
    const range = savedRangeRef.current;
    savedRangeRef.current = null;
    const sel = document.getSelection();
    if (!sel) return;
    sel.removeAllRanges();
    sel.addRange(range);
    if (range.collapsed && execCmd) {
      document.execCommand(execCmd, false);
      requestAnimationFrame(() => el.dispatchEvent(new Event('input', { bubbles: true })));
      return;
    }
    if (range.collapsed) return;
    const wrapper = document.createElement(tagName);
    if (tagName === 'span' && spanClass) wrapper.className = spanClass;
    try {
      range.surroundContents(wrapper);
    } catch {
      const fragment = range.extractContents();
      wrapper.appendChild(fragment);
      range.insertNode(wrapper);
    }
    el.focus();
    requestAnimationFrame(() => el.dispatchEvent(new Event('input', { bubbles: true })));
  };

  const applyFormatContentEditable = (command: string, value?: string) => {
    const el = getTarget();
    if (!el || !el.isConnected || !isContentEditable(el)) return;
    if (savedRangeRef.current) {
      const sel = document.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(savedRangeRef.current);
      }
    }
    if (command === 'bold') {
      wrapSelectionInTag('strong', undefined, 'bold');
      return;
    }
    if (command === 'italic') {
      wrapSelectionInTag('em', undefined, 'italic');
      return;
    }
    if (command === 'strikeThrough') {
      wrapSelectionInTag('span', 'line-through', 'strikeThrough');
      return;
    }
    if (command === 'underline') {
      wrapSelectionInTag('span', 'underline', 'underline');
      return;
    }
    savedRangeRef.current = null;
    el.focus();
    document.execCommand(command, false, value);
    requestAnimationFrame(() => el.dispatchEvent(new Event('input', { bubbles: true })));
  };

  const insertAtCursor = (before: string, after: string = '') => {
    try {
      const target = getTarget();
      if (!target || !target.isConnected) return;
      if (isContentEditable(target)) {
        const cmd = before === '**' && after === '**' ? 'bold' : before === '*' && after === '*' ? 'italic' : before === '~~' && after === '~~' ? 'strikeThrough' : before === '__' && after === '__' ? 'underline' : null;
        if (cmd) {
          applyFormatContentEditable(cmd);
          return;
        }
        if (before === '\n• ' && after === '') {
          applyFormatContentEditable('insertUnorderedList');
          return;
        }
        if (before === '\n1. ' && after === '') {
          applyFormatContentEditable('insertOrderedList');
          return;
        }
      }
      const ta = target as HTMLTextAreaElement;
      if (ta.selectionStart === undefined) return;
      // Use current textarea value so we wrap the actual selection (avoids stale React state = whole paragraph)
      const safeValue = (ta.value != null && typeof ta.value === 'string') ? ta.value : (typeof value === 'string' ? value : '');
      const len = safeValue.length;
      let start: number;
      let end: number;
      if (savedSelectionRef.current) {
        start = Math.min(Math.max(0, savedSelectionRef.current.start), len);
        end = Math.min(Math.max(start, savedSelectionRef.current.end), len);
        savedSelectionRef.current = null;
      } else {
        start = Math.min(Math.max(0, Number(ta.selectionStart) || 0), len);
        end = Math.min(Math.max(start, Number(ta.selectionEnd) || start), len);
      }
      if (Number.isNaN(start)) start = 0;
      if (Number.isNaN(end)) end = start;
      const sel = safeValue.substring(start, end);
      const newText = safeValue.substring(0, start) + before + sel + after + safeValue.substring(end);
      // When no selection and we wrap (before + after), put cursor between markers so typing becomes bold/italic
      const newPos = sel.length > 0
        ? start + before.length + sel.length
        : start + before.length;

      // Update state immediately so editor and preview reflect changes
      if (typeof onChange === 'function') onChange(newText);
      // Defer focus/selection restore so React can re-render first
      requestAnimationFrame(() => {
        try {
          const el = getTextarea();
          if (el?.isConnected) {
            el.focus();
            el.setSelectionRange(newPos, newPos);
          }
        } catch {
          // ignore after unmount
        }
      });
    } catch (err) {
      console.warn('[RichTextToolbar] insertAtCursor failed', err);
    }
  };

  const getTextarea = () => getTarget() as HTMLTextAreaElement | null;

  const btn = 'p-1.5 rounded text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 hover:text-gray-900 dark:hover:text-gray-100 disabled:opacity-50 min-h-[32px] min-w-[32px] touch-manipulation';
  return (
    <div
      className="flex items-center gap-0.5 flex-wrap border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/80 px-1.5 py-1"
      onMouseDownCapture={handleToolbarMouseDown}
      onPointerDownCapture={handleToolbarMouseDown}
    >
      <button type="button" className={btn} disabled={disabled} onClick={(e) => { e.preventDefault(); insertAtCursor('**', '**'); }} title="Bold" aria-label="Bold">
        <Bold className="w-4 h-4" />
      </button>
      <button type="button" className={btn} disabled={disabled} onClick={(e) => { e.preventDefault(); insertAtCursor('*', '*'); }} title="Italic" aria-label="Italic">
        <Italic className="w-4 h-4" />
      </button>
      <button type="button" className={btn} disabled={disabled} onClick={(e) => { e.preventDefault(); insertAtCursor('~~', '~~'); }} title="Strikethrough" aria-label="Strikethrough">
        <Strikethrough className="w-4 h-4" />
      </button>
      <button type="button" className={btn} disabled={disabled} onClick={(e) => { e.preventDefault(); insertAtCursor('__', '__'); }} title="Underline" aria-label="Underline">
        <Underline className="w-4 h-4" />
      </button>
      <span className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-0.5" aria-hidden />
      <button type="button" className={btn} disabled={disabled} onClick={(e) => { e.preventDefault(); insertAtCursor('\n• ', ''); }} title="Bullet list" aria-label="Bullet list">
        <List className="w-4 h-4" />
      </button>
      <button type="button" className={btn} disabled={disabled} onClick={(e) => { e.preventDefault(); insertAtCursor('\n1. ', ''); }} title="Numbered list" aria-label="Numbered list">
        <ListOrdered className="w-4 h-4" />
      </button>
    </div>
  );
};

export default RichTextToolbar;
